import { Component, EventEmitter, Input, Output, SimpleChanges, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { Person, EditPerson } from '../../interfaces/persons.interface';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { MatSnackBar } from '@angular/material/snack-bar';
import { PersonsService } from '../../services/persons.service';
import { UsersService } from '../../services/users.service';

import { DateAdapter } from '@angular/material/core';
import { Subject, takeUntil } from 'rxjs';

import moment from 'moment';

@Component({
  selector: 'person-data',
  standalone: false,
  templateUrl: './data.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./data.component.scss']
})

export class DataComponent implements OnInit, OnDestroy {
  @Output() personChanged = new EventEmitter<EditPerson>();
  @Output() personSaved = new EventEmitter<EditPerson>();
  @Input() person!: Person; // Input property for Person model
  personForm!: FormGroup; // Reactive form
  personId: number = 0; // Initialize personId

  // Date restrictions for birthday field
  maxDate: Date;
  minDate: Date;
  private destroy$ = new Subject<void>();

  constructor(private fb: FormBuilder, private personService: PersonsService, private snackBar: MatSnackBar, private usersService: UsersService) {
    
    // Set date restrictions
    this.maxDate = new Date(); // Start with today
    this.maxDate.setFullYear(this.maxDate.getFullYear() - 18); // Exactly 18 years ago
    this.minDate = new Date();
    this.minDate.setFullYear(this.minDate.getFullYear() - 120); // 120 years ago
    
    // Initialize the form
    this.personForm = this.fb.group({
      default: [0],
      name: ['', [Validators.required, Validators.minLength(2)]],
      name2: ['', [Validators.minLength(1)]],
      surname: ['', [Validators.required, Validators.minLength(3)]],
      phone: ['', [Validators.required, Validators.pattern(/^[0-9]{9,15}$/)]], // Optional phone validation
      birthday: ['', Validators.required],
      type: ['', Validators.required],
      accept_news: [0],
      street: [''],
      city: ['Zielona Góra'],
      home: [''],
      postcode: ['65-001', [Validators.pattern(/^\d{2}-\d{3}$/)]], // Polish postcode format
    });
  }

  ngOnInit(): void {
    // Listen to type changes to adjust birthday date range
    this.personForm.get('type')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        if (type === 'STUDENT') {
          // Students: 18 to 26 years old
          this.maxDate = new Date();
          this.maxDate.setFullYear(this.maxDate.getFullYear() - 15);
          this.minDate = new Date();
          this.minDate.setFullYear(this.minDate.getFullYear() - 26);
        } else {
          // Other types: minimum 18 years old, maximum 120 years old
          this.maxDate = new Date();
          this.maxDate.setFullYear(this.maxDate.getFullYear() - 15);
          this.minDate = new Date();
          this.minDate.setFullYear(this.minDate.getFullYear() - 120);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasCards(): boolean {
    return !!this.person && Array.isArray(this.person.cards) && this.person.cards.length > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if the person input has changed
    if (changes['person'] && changes['person'].currentValue) {
      this.updateForm(changes['person'].currentValue);
      this.personId = changes['person'].currentValue.id || 0; // Update personId if available
    }
  }

  updateForm(person: Person): void {
    // Update the form with the new person data
      // Format birthday to only date (YYYY-MM-DD) if present

    
    this.personForm.patchValue({ 
      default: person.default || 0,
      name: person.name || '',  
      name2: person.name2 || '',  
      surname: person.surname || '', 
      phone: person.phone || '',
      birthday:  person.birthday || '',
      type: person.type || '',
      street: person.street || '',
      city: person.city || 'Zielona Góra',
      home: person.home || '',
      accept_news: person.accept_news || 0,
      postcode: person.postcode || '65-001',
    });

    // Update maxDate based on person type
    if (person.type === 'STUDENT') {
      // Students: 18 to 26 years old
      this.maxDate = new Date();
      this.maxDate.setFullYear(this.maxDate.getFullYear() - 18);
      this.minDate = new Date();
      this.minDate.setFullYear(this.minDate.getFullYear() - 26);
    } else if (person.type) {
      // Other types: minimum 18 years old, maximum 120 years old
      this.maxDate = new Date();
      this.maxDate.setFullYear(this.maxDate.getFullYear() - 18);
      this.minDate = new Date();
      this.minDate.setFullYear(this.minDate.getFullYear() - 120);
    }

    // Mark all fields as touched to show validation errors immediately
    Object.keys(this.personForm.controls).forEach(key => {
      this.personForm.get(key)?.markAsTouched();
    });
  }

  onSubmit(): void {
    if (this.personForm.valid) {

      const person: EditPerson = { ...this.personForm.value };

      if (person.birthday) {
            // If it's a string, parse as local date
            let date = new Date(person.birthday)

            // Adjust for timezone offset to get local date as YYYY-MM-DD
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            person.birthday = `${year}-${month}-${day}`;
        }



        //person.accept_news = this.personForm.value.accept_news === true ? 1 : 0;
   
          const request$ = this.personId
            ? this.personService.updatePerson(this.personId, person)
            : this.personService.createPerson(person);

          request$.subscribe({
            next: (response: any) => {


              this.personSaved.emit(response); // Emit event to notify parent component
              this.personChanged.emit(response); 
            },
            
            error: (err: any) => {
              let errorMessage = err.error?.detail || 'An error occurred';
              if (err.status === 400) {
                errorMessage = 'Błędne dane. Sprawdź formularz.';
              } else if (err.status === 404) {
                errorMessage = 'Nie znaleziono mieszkańca.';
              } else if (err.status === 500) {
                errorMessage = 'Błąd serwera. Spróbuj ponownie później.';
              } else if (err.status === 409) {
                errorMessage = 'Mieszkaniec z tymi danymi już istnieje. Skontaktuj się z obsługą klienta. ';
              }
    
              this.snackBar.open(errorMessage, 'Zamknij', {
                duration: 20000,
                panelClass: ['error-snackbar'],
                horizontalPosition: 'center', // Center horizontally
                verticalPosition: 'bottom',
              });
            },
          });
          
    }
  }

}
